import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { PhoneInTalk } from '@mui/icons-material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNumberFormat } from '../hooks/useNumberFormat';
import type { CallConsumptionSegment, CallSegmentDetail, CallSegmentType, CallStatus } from '../types';

export type BillableConsumptionBucketKey = 'ai' | 'telephony';

export interface BillableConsumptionBucket {
  key: BillableConsumptionBucketKey;
  billedSeconds: number;
  grossBilledSeconds?: number;
  refundedSeconds?: number;
  effectiveSeconds: number;
  isActive?: boolean;
  approxSeconds?: number | null;
}

interface CallConsumptionDisplayProps {
  segments?: CallConsumptionSegment[] | null;
  segmentDetails?: CallSegmentDetail[] | null;
  billableBuckets?: BillableConsumptionBucket[] | null;
  activeSegmentId?: string | null;
  activeSegmentType?: CallSegmentType | null;
  callStatus?: CallStatus | null;
  fallbackBilledSeconds?: number | null;
  fallbackDurationSeconds?: number | null;
  fallbackBucketType?: BillableConsumptionBucketKey;
  compact?: boolean;
  singleLine?: boolean;
}

interface InternalBillableBucket extends BillableConsumptionBucket {
  approxCandidates: Array<{
    activeSegmentDurationSeconds: number;
    elapsedSeconds: number;
  }>;
}

const BILLABLE_BUCKET_ORDER: BillableConsumptionBucketKey[] = ['ai', 'telephony'];

function getBucketKeyForSegmentType(segmentType: CallSegmentType): BillableConsumptionBucketKey | null {
  if (segmentType === 'ai') return 'ai';
  if (
    segmentType === 'telephony_internal_extension'
    || segmentType === 'telephony_outbound_internal_extension'
    || segmentType === 'telephony_external_extension'
    || segmentType === 'telephony_external_phone'
    || segmentType === 'ivr'
  ) {
    return 'telephony';
  }
  return null;
}

function getBucketIcon(bucketKey: BillableConsumptionBucketKey) {
  if (bucketKey === 'telephony') {
    return <PhoneInTalk sx={{ fontSize: 14 }} />;
  }
  return <PsychologyIcon sx={{ fontSize: 14 }} />;
}

function getRefundedSeconds(
  bucketOrSegment: Pick<BillableConsumptionBucket, 'refundedSeconds'> | Pick<CallConsumptionSegment, 'refundedSeconds'>,
): number {
  return Math.max(Number(bucketOrSegment.refundedSeconds || 0), 0);
}

function getGrossBilledSeconds(
  bucketOrSegment:
    | Pick<BillableConsumptionBucket, 'grossBilledSeconds' | 'billedSeconds' | 'refundedSeconds'>
    | Pick<CallConsumptionSegment, 'grossBilledSeconds' | 'billedSeconds' | 'refundedSeconds'>,
): number {
  if (bucketOrSegment.grossBilledSeconds != null) {
    return Math.max(Number(bucketOrSegment.grossBilledSeconds || 0), 0);
  }
  return Math.max(Number(bucketOrSegment.billedSeconds || 0), 0) + getRefundedSeconds(bucketOrSegment);
}

function callStatusAllowsApproximation(callStatus?: CallStatus | null): boolean {
  if (!callStatus) return true;
  return (
    callStatus === 'pending'
    || callStatus === 'queued'
    || callStatus === 'dialing'
    || callStatus === 'analyzing'
    || callStatus === 'ringing'
    || callStatus === 'in_progress'
  );
}

function aggregateSegmentsIntoBuckets(
  segments: CallConsumptionSegment[],
  segmentDetailById: Map<string, CallSegmentDetail>,
  callAllowsApprox: boolean,
  nowMs: number,
): BillableConsumptionBucket[] {
  const buckets = new Map<BillableConsumptionBucketKey, InternalBillableBucket>();

  const getOrCreateBucket = (bucketKey: BillableConsumptionBucketKey): InternalBillableBucket => {
    const existing = buckets.get(bucketKey);
    if (existing) return existing;
    const created: InternalBillableBucket = {
      key: bucketKey,
      billedSeconds: 0,
      grossBilledSeconds: 0,
      refundedSeconds: 0,
      effectiveSeconds: 0,
      isActive: false,
      approxSeconds: null,
      approxCandidates: [],
    };
    buckets.set(bucketKey, created);
    return created;
  };

  segments.forEach((segment) => {
    const bucketKey = getBucketKeyForSegmentType(segment.segmentType);
    if (!bucketKey) return;

    const bucket = getOrCreateBucket(bucketKey);
    const billedSeconds = Math.max(Number(segment.billedSeconds || 0), 0);
    const refundedSeconds = getRefundedSeconds(segment);
    const grossBilledSeconds = getGrossBilledSeconds(segment);
    const effectiveSeconds = Math.max(Number(segment.durationSeconds || 0), 0);

    bucket.billedSeconds += billedSeconds;
    bucket.refundedSeconds = (bucket.refundedSeconds || 0) + refundedSeconds;
    bucket.grossBilledSeconds = (bucket.grossBilledSeconds || 0) + grossBilledSeconds;
    bucket.effectiveSeconds += effectiveSeconds;
    bucket.isActive = Boolean(bucket.isActive || segment.isActive);

    if (!callAllowsApprox || !segment.isActive) return;

    const detail = segmentDetailById.get(segment.id);
    const startedAtMs = detail?.startedAt ? Date.parse(detail.startedAt) : Number.NaN;
    if (!Number.isFinite(startedAtMs) || detail?.endedAt) return;

    bucket.approxCandidates.push({
      activeSegmentDurationSeconds: effectiveSeconds,
      elapsedSeconds: Math.max(0, Math.floor((nowMs - startedAtMs) / 1000)),
    });
  });

  return BILLABLE_BUCKET_ORDER.flatMap((bucketKey) => {
    const bucket = buckets.get(bucketKey);
    if (!bucket) return [];

    const approxSeconds = bucket.approxCandidates.length > 0
      ? Math.max(
        ...bucket.approxCandidates.map((candidate) => Math.max(
          0,
          bucket.effectiveSeconds - candidate.activeSegmentDurationSeconds + candidate.elapsedSeconds,
        )),
      )
      : null;

    return [{
      key: bucket.key,
      billedSeconds: bucket.billedSeconds,
      grossBilledSeconds: bucket.grossBilledSeconds,
      refundedSeconds: bucket.refundedSeconds,
      effectiveSeconds: bucket.effectiveSeconds,
      isActive: bucket.isActive,
      approxSeconds,
    }];
  });
}

function normalizeBillableBuckets(
  billableBuckets: BillableConsumptionBucket[] | null | undefined,
): BillableConsumptionBucket[] {
  if (!Array.isArray(billableBuckets) || billableBuckets.length === 0) {
    return [];
  }

  const normalizedByKey = new Map<BillableConsumptionBucketKey, BillableConsumptionBucket>();

  billableBuckets.forEach((bucket) => {
    normalizedByKey.set(bucket.key, {
      key: bucket.key,
      billedSeconds: Math.max(Number(bucket.billedSeconds || 0), 0),
      grossBilledSeconds: getGrossBilledSeconds(bucket),
      refundedSeconds: getRefundedSeconds(bucket),
      effectiveSeconds: Math.max(Number(bucket.effectiveSeconds || 0), 0),
      isActive: Boolean(bucket.isActive),
      approxSeconds: bucket.approxSeconds != null ? Math.max(Number(bucket.approxSeconds || 0), 0) : null,
    });
  });

  return BILLABLE_BUCKET_ORDER.flatMap((bucketKey) => {
    const bucket = normalizedByKey.get(bucketKey);
    return bucket ? [bucket] : [];
  });
}

function hasRenderableConsumption(bucket: BillableConsumptionBucket): boolean {
  return (
    bucket.billedSeconds > 0
    || bucket.effectiveSeconds > 0
    || Math.max(Number(bucket.approxSeconds || 0), 0) > 0
    || Boolean(bucket.isActive)
  );
}

export default function CallConsumptionDisplay({
  segments,
  segmentDetails,
  billableBuckets,
  activeSegmentId,
  activeSegmentType,
  callStatus,
  fallbackBilledSeconds,
  fallbackDurationSeconds,
  fallbackBucketType = 'ai',
  compact = false,
  singleLine = true,
}: CallConsumptionDisplayProps) {
  const { t } = useTranslation(['components', 'common']);
  const { formatDuration } = useNumberFormat();
  const [nowMs, setNowMs] = useState(() => Date.now());
  const fallbackBilled = Math.max(Number(fallbackBilledSeconds || 0), 0);
  const fallbackDuration = Math.max(Number(fallbackDurationSeconds || 0), 0);
  void activeSegmentId;
  void activeSegmentType;

  const callAllowsApprox = useMemo(() => callStatusAllowsApproximation(callStatus), [callStatus]);

  const segmentDetailById = useMemo(() => {
    if (!Array.isArray(segmentDetails) || segmentDetails.length === 0) {
      return new Map<string, CallSegmentDetail>();
    }
    return new Map<string, CallSegmentDetail>(segmentDetails.map((detail) => [detail.id, detail]));
  }, [segmentDetails]);

  const aggregatedBuckets = useMemo(() => {
    if (Array.isArray(billableBuckets) && billableBuckets.length > 0) {
      return normalizeBillableBuckets(billableBuckets);
    }
    if (!Array.isArray(segments) || segments.length === 0) {
      return [];
    }
    return aggregateSegmentsIntoBuckets(segments, segmentDetailById, callAllowsApprox, nowMs);
  }, [billableBuckets, segments, segmentDetailById, callAllowsApprox, nowMs]);

  const shouldTickApprox = useMemo(
    () => aggregatedBuckets.some((bucket) => Math.max(Number(bucket.approxSeconds || 0), 0) > 0),
    [aggregatedBuckets],
  );

  useEffect(() => {
    if (!shouldTickApprox) return;
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [shouldTickApprox]);

  const shouldUseFallback = !aggregatedBuckets.some(hasRenderableConsumption)
    && (fallbackBilled > 0 || fallbackDuration > 0);

  const normalizedBuckets = shouldUseFallback
    ? [{
      key: fallbackBucketType,
      billedSeconds: fallbackBilled,
      grossBilledSeconds: fallbackBilled,
      refundedSeconds: 0,
      effectiveSeconds: fallbackDuration,
      isActive: false,
      approxSeconds: null,
    } satisfies BillableConsumptionBucket]
    : aggregatedBuckets;

  if (!normalizedBuckets.some(hasRenderableConsumption)) {
    return <Typography variant="body2" color="text.secondary">-</Typography>;
  }

  return (
    <Stack
      spacing={compact ? 0.5 : 1}
      direction={singleLine ? 'row' : 'column'}
      useFlexGap
      sx={
        singleLine
          ? {
            flexWrap: 'nowrap',
            alignItems: 'center',
            maxWidth: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
          }
          : undefined
      }
    >
      {normalizedBuckets.map((bucket) => {
        const billedSeconds = Math.max(Number(bucket.billedSeconds || 0), 0);
        const grossBilledSeconds = getGrossBilledSeconds(bucket);
        const refundedSeconds = getRefundedSeconds(bucket);
        const effectiveSeconds = Math.max(Number(bucket.effectiveSeconds || 0), 0);
        const approxSeconds = bucket.approxSeconds != null ? Math.max(Number(bucket.approxSeconds || 0), 0) : null;
        const billed = formatDuration(billedSeconds);
        const grossBilled = formatDuration(grossBilledSeconds);
        const refunded = formatDuration(refundedSeconds);
        const effective = formatDuration(effectiveSeconds);
        const approx = approxSeconds != null ? `~${formatDuration(approxSeconds)}` : null;
        const hasRefundBreakdown = refundedSeconds > 0 || grossBilledSeconds !== billedSeconds;
        const tooltipTitle = approx
          ? t('components:callConsumption.bucketTooltipApprox', {
            type: t(`components:callConsumption.types.${bucket.key}`),
            approx,
          })
          : hasRefundBreakdown
            ? t('components:callConsumption.bucketTooltipRefunded', {
              type: t(`components:callConsumption.types.${bucket.key}`),
              gross: grossBilled,
              refunded,
              net: billed,
              effective,
            })
            : t('components:callConsumption.bucketTooltip', {
              type: t(`components:callConsumption.types.${bucket.key}`),
              billed,
              effective,
            });

        return (
          <Box
            key={bucket.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: 1,
              py: compact ? 0.5 : 0.75,
              borderRadius: 1,
              border: '1px solid',
              borderColor: bucket.isActive ? 'primary.main' : 'divider',
              bgcolor: bucket.isActive ? 'action.selected' : 'background.paper',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              {getBucketIcon(bucket.key)}
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ textTransform: 'uppercase', whiteSpace: 'nowrap' }}
              >
                {t(`components:callConsumption.${compact ? 'typesShort' : 'types'}.${bucket.key}`)}
              </Typography>
              {bucket.isActive && (
                <Tooltip title={t('components:callConsumption.active')} arrow>
                  <Box
                    data-testid={`active-dot-${bucket.key}`}
                    aria-label={t('components:callConsumption.active')}
                    sx={{
                      position: 'relative',
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}`,
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: -4,
                        borderRadius: '50%',
                        border: (theme) => `2px solid ${theme.palette.success.main}`,
                        opacity: 0.7,
                        animation: 'call-consumption-active-pulse 1.4s ease-out infinite',
                      },
                      '@keyframes call-consumption-active-pulse': {
                        '0%': { transform: 'scale(0.6)', opacity: 0.7 },
                        '70%': { transform: 'scale(1.2)', opacity: 0 },
                        '100%': { transform: 'scale(1.2)', opacity: 0 },
                      },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
            <Tooltip title={tooltipTitle} arrow>
              {approx ? (
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'text.primary' }}>
                  {approx}
                </Typography>
              ) : (
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    display: 'inline-flex',
                    alignItems: 'baseline',
                    gap: 0.25,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 700,
                      color: hasRefundBreakdown ? 'warning.main' : 'text.primary',
                    }}
                  >
                    {billed}
                  </Box>
                  <Box component="span" sx={{ color: 'text.secondary' }}>/</Box>
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {effective}
                  </Box>
                </Typography>
              )}
            </Tooltip>
          </Box>
        );
      })}
    </Stack>
  );
}
